import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { useApi } from '@backstage/core-plugin-api';
import { RepoUrlPickerState } from '../../interfaces/RepoUrlPickerState';
import {
  AlmServicesPluginApiRef,
  RepositoryTypeItem,
} from '@internal/plugin-alm-services';
interface AutocompleteOption {
  label: string;
  id: string;
}

interface RepositoryTypeProps {
  repositoryType?: string;
  onChange: (state: RepoUrlPickerState) => void;
  rawErrors: string[];
}
export default function RepositoryType(
  props: Readonly<RepositoryTypeProps>,
): JSX.Element {
  const config = useApi(AlmServicesPluginApiRef);
  const { repositoryType, onChange, rawErrors } = props;
  const [repositoryTypes, setRepositoryTypes] = useState<RepositoryTypeItem[]>(
    [],
  );
  const [currentSelection, setCurrentSelection] =
    useState<AutocompleteOption | null>(null);

  const fetchRepositoryTypes = async () => {
    const repoTypes = await config.repositoryTypes();

    setRepositoryTypes(repoTypes.data ?? []);
  };

  useEffect(() => {
    fetchRepositoryTypes();
  }, []);

  useEffect(() => {
    if (repositoryTypes.length > 0) {
      const currentValue = repositoryTypes.find(
        x => x.prefix === repositoryType,
      );
      setCurrentSelection(
        repositoryType === '' || repositoryType === undefined
          ? null
          : {
              id: currentValue?.id ?? '',
              label: `${currentValue?.prefix} - (${currentValue?.description})`,
            },
      );
    }
  }, [repositoryType, repositoryTypes]);

  const renderMenuItems = useMemo(
    () =>
      repositoryTypes.map<AutocompleteOption>(type => ({
        id: type.prefix,
        label: `${type.prefix} - (${type.description})`,
      })),
    [repositoryTypes],
  );

  const handleOnChange = useCallback((e: any, value: any) => {
    onChange({
      repositoryType: value?.id,
    });
    setCurrentSelection(value);
  }, []);

  return (
    <FormControl
      margin="normal"
      required
      error={rawErrors?.length > 0 && !repositoryType}
    >
      <Autocomplete
        getOptionLabel={option => option.label}
        onChange={handleOnChange}
        options={renderMenuItems}
        value={currentSelection}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id;
        }}
        renderInput={params => (
          <TextField {...params} label="Repository Type *" />
        )}
        data-testid="azure-devops-repository-type-select"
      />

      <FormHelperText>
        <p>Choose the application type (This will be the Repository prefix)</p>
        <Link
          target="_blank"
          underline="hover"
          href="https://dev.azure.com/ITGlo/DevSecOps/_wiki/wikis/DevSecOps.wiki/1217/Repository-Prefixes"
        >
          More Info
        </Link>
      </FormHelperText>
    </FormControl>
  );
}
